"use client"

import { useEffect, useState } from "react"
import Link from "next/link"
import { useTranslations } from "next-intl"

import { Button } from "@/components/ui/button"

const ACK_COOKIE = "cookie-notice"

/**
 * Tells the reader which cookies the app sets, once.
 *
 * ── A notice, not a choice ───────────────────────────────────────────────────────────────
 *
 * It used to be a consent gate with Accept All and Accept Necessary, and the answer decided one
 * thing: whether Google Analytics loaded. Google Analytics is gone, and every cookie the app
 * still sets is one it cannot work without — the session, the language, the preview gate — plus
 * this one, recording that the notice has been read.
 *
 * So there is nothing left to decline, and offering the button anyway would be the worse kind of
 * consent theatre: a choice presented as meaningful whose two branches are identical. Under
 * ePrivacy, strictly necessary cookies need to be DISCLOSED, not agreed to, which is what this
 * does.
 *
 * If an optional cookie is ever added — a third-party analytics script, an embedded player, an
 * ad pixel — this has to become a real gate again, with the optional thing withheld until the
 * reader agrees. That is a different component and it should not be grafted onto this one by
 * adding a second button.
 *
 * ── Why it is not `react-cookie-consent` any more ────────────────────────────────────────
 *
 * That library's whole value is the accept/decline machinery this no longer has, and it came
 * with its own styling that had to be disabled prop by prop. What is left is a fixed div, a
 * cookie, and a button.
 */
export function CookieBanner() {
  const t = useTranslations("cookies")
  const [visible, setVisible] = useState(false)

  /*
    Mounted hidden and shown only once the cookie has been read, rather than shown and then
    hidden: the server has no idea whether this reader has seen it, so rendering it visible would
    flash the banner at everyone who already dismissed it, on every page load.
  */
  useEffect(() => {
    const seen = document.cookie.split("; ").some((c) => c.startsWith(`${ACK_COOKIE}=`))
    if (!seen) setVisible(true)
  }, [])

  if (!visible) return null

  const dismiss = () => {
    // A year, and `SameSite=Lax` — it is read by this site's own pages and nothing else.
    document.cookie = `${ACK_COOKIE}=1; path=/; max-age=${60 * 60 * 24 * 365}; SameSite=Lax`
    setVisible(false)
  }

  return (
    <div
      role="region"
      aria-label={t("noticeLabel")}
      className="fixed inset-x-0 bottom-0 z-50 flex flex-col items-center justify-between gap-4 border-t border-border bg-background px-6 py-5 shadow-[0_-4px_24px_rgba(0,0,0,0.06)] sm:flex-row sm:gap-8"
    >
      <p className="text-sm leading-relaxed text-muted-foreground">
        {t("notice")}{" "}
        <Link href="/privacy" className="underline underline-offset-4 hover:text-foreground">
          {t("learnMore")}
        </Link>
        .
      </p>
      <Button onClick={dismiss} className="shrink-0">
        {t("dismiss")}
      </Button>
    </div>
  )
}
